import { Cross1Icon } from '@radix-ui/react-icons';
import { Badge, Box, Flex, Text } from '@radix-ui/themes';
import { KeyboardEvent, useMemo, useRef, useState } from 'react';
import { useListNavigation } from '@common/hooks/useListNavigation';
import { SuggestionDropdown } from '@common/components/SuggestionDropdown/SuggestionDropdown';

export interface TagInputOptions {
    placeholder?: string;
    suggestions?: string[];
    validate?: (tag: string) => boolean;
    separators?: string[];
    submitKeys?: string[];
}

export interface TagInputProps {
    tags: string[];
    onChange: (tags: string[]) => void;
    options?: TagInputOptions;
}

export const TagInput = ({ tags, onChange, options = {} }: TagInputProps) => {
    const {
        placeholder = '',
        suggestions = [],
        validate = () => true,
        separators = [',', ';', '\n'],
        submitKeys = ['Enter', ',', ';'],
    } = options;

    const [inputValue, setInputValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const availableSuggestions = suggestions
        .filter(
            (s) =>
                s.toLowerCase().includes(inputValue.toLowerCase()) &&
                !tags.includes(s),
        )
        .slice(0, 5);

    const showDropdown = isFocused && availableSuggestions.length > 0;

    const splitRegex = useMemo(() => {
        if (separators.length === 0) return null;
        const escaped = separators
            .map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
            .join('');
        return new RegExp(`[${escaped}]+`);
    }, [separators]);

    const addTags = (rawInput: string) => {
        const parsedTags = splitRegex ? rawInput.split(splitRegex) : [rawInput];
        const newTags = parsedTags
            .map((t) => t.trim())
            .filter((t) => t.length > 0 && !tags.includes(t));

        if (newTags.length > 0) onChange([...tags, ...newTags]);
        setInputValue('');
    };

    const {
        highlightedIndex,
        setHighlightedIndex,
        handleKeyDown: listHandleKeyDown,
    } = useListNavigation({
        itemCount: availableSuggestions.length,
        isOpen: showDropdown,
        onSelect: (index) => addTags(availableSuggestions[index]),
        onClose: () => setIsFocused(false),
    });

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        // TagInput specific submit keys
        if (submitKeys.includes(e.key)) {
            e.preventDefault();
            addTags(
                showDropdown && availableSuggestions[highlightedIndex]
                    ? availableSuggestions[highlightedIndex]
                    : inputValue,
            );
            return;
        }

        // TagInput specific backspace behavior
        if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) {
            onChange(tags.slice(0, -1));
            return;
        }

        // Delegate arrow navigation to our shared hook
        listHandleKeyDown(e);
    };

    return (
        <Flex
            wrap='wrap'
            gap='2'
            align='center'
            style={{
                minHeight: '40px',
                padding: 'var(--space-1) var(--space-2)',
                border: `1px solid ${isFocused ? 'var(--accent-8)' : 'var(--gray-a7)'}`,
                outline: isFocused ? '1px solid var(--accent-8)' : 'none',
                borderRadius: 'var(--radius-2)',
                backgroundColor: 'var(--color-surface)',
                position: 'relative',
                cursor: 'text',
            }}
            onClick={() => inputRef.current?.focus()}
        >
            {tags.map((tag) => (
                <Badge
                    key={tag}
                    size='2'
                    color={validate(tag) ? 'gray' : 'ruby'}
                    variant='surface'
                >
                    <Flex align='center' gap='1'>
                        {tag}
                        <Cross1Icon
                            style={{
                                cursor: 'pointer',
                                width: '12px',
                                height: '12px',
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                onChange(tags.filter((t) => t !== tag));
                            }}
                        />
                    </Flex>
                </Badge>
            ))}

            <input
                ref={inputRef}
                type='text'
                value={inputValue}
                placeholder={tags.length === 0 ? placeholder : ''}
                onChange={(e) => {
                    setInputValue(e.target.value);
                    setHighlightedIndex(0); // Reset index declaratively
                }}
                onKeyDown={handleKeyDown}
                onPaste={(e) => {
                    e.preventDefault();
                    addTags(e.clipboardData.getData('text'));
                }}
                onFocus={() => setIsFocused(true)}
                onBlur={() => {
                    setIsFocused(false);
                    if (inputValue.trim()) addTags(inputValue);
                }}
                style={{
                    flex: 1,
                    minWidth: '150px',
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    color: 'var(--gray-12)',
                    fontSize: 'var(--font-size-3)',
                    fontFamily: 'inherit',
                }}
            />

            {showDropdown && (
                <SuggestionDropdown<string>
                    items={availableSuggestions}
                    highlightedIndex={highlightedIndex}
                    onHighlight={setHighlightedIndex}
                    onSelect={addTags}
                    highlightColor='var(--accent-a3)'
                    renderItem={(item) => (
                        <Box
                            style={{ padding: 'var(--space-2) var(--space-3)' }}
                        >
                            <Text size='2'>{item}</Text>
                        </Box>
                    )}
                />
            )}
        </Flex>
    );
};
